import React from 'react';

interface LinkState {
	tooltipPosition: { x: number; y: number };
	showTooltip: boolean;
	linkId: number | null;
	linkPage: number | null;
}

interface UseInternalLinksParams {
	featureFlagInternalLink?: boolean;
}

interface UseInternalLinksReturn {
	linkState: LinkState;
	setLinkState: React.Dispatch<React.SetStateAction<LinkState>>;
	handleInternalLinkElement: (anchor: HTMLAnchorElement) => boolean;
	hideLinkTooltip: () => void;
}

const internalLink = import.meta.env.VITE_INTERNAL_LINK;

export const useInternalLinks = ({
	featureFlagInternalLink
}: UseInternalLinksParams): UseInternalLinksReturn => {
	const [linkState, setLinkState] = React.useState<LinkState>({
		tooltipPosition: { x: 0, y: 0 },
		showTooltip: false,
		linkId: null,
		linkPage: null
	});

	const handleInternalLinkElement = React.useCallback((anchor: HTMLAnchorElement) => {
		if (!featureFlagInternalLink) {
			return false;
		}

		const url = anchor.href || '';
		const isInternalLink = url.toLowerCase().startsWith(internalLink.toLowerCase());

		if (isInternalLink) {
			const parsedUrl = new URL(url);
			const hash = parsedUrl.hash.substring(1);
			const [, query] = hash.split('?');

			if (query) {
				const params = new URLSearchParams(query);
				const id = params.get('id');
				const page = params.get('page');
				const rect = anchor.getBoundingClientRect();
				const containerRect = anchor.closest('.react-pdf__Page')?.getBoundingClientRect() ||
					{ left: 0, top: 0 };

				const linkCenterX = rect.left - containerRect.left + rect.width / 2;
				const linkTop = rect.top - containerRect.top;

				setLinkState({
					tooltipPosition: { x: linkCenterX, y: linkTop },
					showTooltip: true,
					linkId: parseInt(id || '0'),
					linkPage: parseInt(page || '0')
				});

				return true; // Indicates this was an internal link
			}
		}
		return false; // Not an internal link
	}, [featureFlagInternalLink]);

	const hideLinkTooltip = React.useCallback(() => {
		setLinkState(prev => ({ ...prev, showTooltip: false }));
	}, []);

	return {
		linkState,
		setLinkState,
		handleInternalLinkElement,
		hideLinkTooltip
	};
};
